import React, { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import axios from 'axios';
import authConfig from '../../authConfig';
import SecureFileLink from '../meeting/SecureFileLink';
import { Container, Row, Col, Table, Button, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaEdit, FaRegFileAlt, FaRegTrashAlt, FaToggleOff, FaToggleOn } from 'react-icons/fa';

function MemberDocumentLibraryAdminPage() {
    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const account = useAccount(accounts[0] || {});

    const [items, setItems] = useState([]);
    const [sortField, setSortField] = useState('title');
    const [sortAsc, setSortAsc] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [saving, setSaving] = useState(false);

    const acquireToken = async () => {
        if (!isAuthenticated) return null;
        try {
            const response = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            return response?.accessToken ?? null;
        } catch (err) {
            console.error('token acquire failed', err);
            return null;
        }
    };
    function extractErrorMessage(err) {
        if (!err) return 'unknown error';
        const data = err.response?.data ?? err.data ?? null;
        const parsed = parseApiErrors(data);
        if (parsed) return parsed;
        return err.message || String(err);
    }
    function parseApiErrors(data) {
        if (!data) return null;
        // modelstate style
        if (data.errors && typeof data.errors === 'object') {
            const parts = [];
            for (const key of Object.keys(data.errors)) {
                const arr = data.errors[key];
                if (Array.isArray(arr)) {
                    for (const msg of arr) parts.push(`${key}: ${msg}`);
                } else if (typeof arr === 'string') {
                    parts.push(`${key}: ${arr}`);
                }
            }
            return parts.join('\n');
        }
        // generic error shape
        if (data.title && data.detail) return `${data.title}: ${data.detail}`;
        if (data.title) return data.title;
        if (typeof data === 'string') return data;
        return JSON.stringify(data);
    }

    useEffect(() => { loadData(); // eslint-disable-next-line
    }, []);

    function changeSort(field) {
        if (sortField === field) {
            setSortAsc(prev => !prev);
        } else {
            setSortField(field);
            setSortAsc(true);
        }
    }

    async function loadData() {
        setError(null);
        setLoading(true);
        try {
            const token = await acquireToken();
            const axiosInstance = axios.create({ headers: token ? { Authorization: `Bearer ${token}` } : {} });
            const resp = await axiosInstance.get(authConfig.api.urls.getMemberLibraryDocumentList);
            const arr = Array.isArray(resp.data) ? resp.data : [];
            setItems(arr);
        } catch (err) {
            console.error(err);
            setError('Failed to load documents: ' + (err?.message || 'unknown'));
        } finally {
            setLoading(false);
        }
    }

    function openAdd() {
        setEditingItem({ title: '', description: '', category: '', fileName: '', enabled: true });
        setSelectedFile(null);
        setShowEditModal(true);
    }

    function openEdit(item) {
        setEditingItem({ ...item });
        setSelectedFile(null);
        setShowEditModal(true);
    }

    function closeModal() {
        setShowEditModal(false);
        setEditingItem(null);
        setSaving(false);
        setSelectedFile(null);
        setError(null);
    }

    function onFieldChange(e) {
        const { name, value, type, checked } = e.target;
        setEditingItem(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }

    async function saveItem() {
        setError(null);
        if (!editingItem) return;
        if (!editingItem.title || editingItem.title.trim() === '') {
            setError('Title is required');
            return;
        }
        if (!editingItem.id && !selectedFile) {
            setError('File is required');
            return;
        }
        if (selectedFile && !isPdfFile(selectedFile)) {
            setError('Only PDF files are allowed');
            return;
        }
        setSaving(true);
        const token = await acquireToken();
        if (!token) { setSaving(false); return; }
        const axiosInstance = axios.create({ headers: { Authorization: `Bearer ${token}` } });
        try {
            if (editingItem.id) {
                await axiosInstance.put(`${authConfig.api.urls.getMemberLibraryDocument}/${editingItem.id}`, editingItem);
                // If a new file was selected while editing, upload it as part of save
                if (selectedFile) {
                    try {
                        const formData = new FormData();
                        formData.append('id', editingItem.id);
                        formData.append('file', selectedFile);
                        await axiosInstance.post(authConfig.api.urls.getMemberLibraryDocumentUploadFile, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setSelectedFile(null);
                    } catch (uploadErr) {
                        console.error('Upload during save failed', uploadErr);
                        setError('Upload failed: ' + extractErrorMessage(uploadErr));
                        setSaving(false);
                        return;
                    }
                }
                await loadData();
                closeModal();
            } else {
                const formData = new FormData();
                formData.append('title', editingItem.title || '');
                formData.append('description', editingItem.description || '');
                formData.append('category', editingItem.category || '');
                formData.append('enabled', editingItem.enabled ? 'true' : 'false');
                formData.append('file', selectedFile);
                await axiosInstance.post(authConfig.api.urls.getMemberLibraryDocument, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                await loadData();
                closeModal();
            }
        } catch (err) {
            console.error(err);
            setError('Save failed: ' + extractErrorMessage(err));
        } finally {
            setSaving(false);
        }
    }

    async function deleteItem(item) {
        if (!window.confirm(`Delete \"${item.title}\"?`)) return;
        setError(null);
        const token = await acquireToken();
        if (!token) return;
        const axiosInstance = axios.create({ headers: { Authorization: `Bearer ${token}` } });
        try {
            await axiosInstance.delete(`${authConfig.api.urls.getMemberLibraryDocument}/${item.id}`);
            await loadData();
        } catch (err) {
            console.error(err);
            setError('Delete failed: ' + (err?.message || 'unknown'));
        }
    }

    async function toggleEnabled(item) {
        setError(null);
        const token = await acquireToken();
        if (!token) return;
        const axiosInstance = axios.create({ headers: { Authorization: `Bearer ${token}` } });
        try {
            const updated = { ...item, enabled: !item.enabled };
            await axiosInstance.put(`${authConfig.api.urls.getMemberLibraryDocument}/${item.id}`, updated);
            await loadData();
        } catch (err) {
            console.error(err);
            setError('Update failed: ' + (err?.message || 'unknown'));
        }
    }

    // File upload when editing an existing document
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    function isPdfFile(file) {
        if (!file) return false;
        const name = (file.name || '').toString().toLowerCase();
        return file.type === 'application/pdf' || name.endsWith('.pdf');
    }

    function onFileChange(e) {
        const f = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
        if (f && !isPdfFile(f)) {
            setSelectedFile(null);
            setError('Only PDF files are allowed');
            return;
        }
        setSelectedFile(f);
        setError(null);
    }

    async function uploadFile() {
        setError(null);
        if (!editingItem || !editingItem.id) {
            setError('Save the document record before uploading a file.');
            return;
        }
        if (!selectedFile) {
            setError('No file selected.');
            return;
        }
        if (!isPdfFile(selectedFile)) {
            setError('Only PDF files are allowed');
            return;
        }
        setUploading(true);
        const token = await acquireToken();
        if (!token) { setUploading(false); return; }
        try {
            const axiosInstance = axios.create({ headers: { Authorization: `Bearer ${token}` } });
            const formData = new FormData();
            formData.append('id', editingItem.id);
            formData.append('file', selectedFile);
            const resp = await axiosInstance.post(authConfig.api.urls.getMemberLibraryDocumentUploadFile, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            const updated = resp?.data || { ...editingItem, fileName: selectedFile.name, FileName: selectedFile.name };
            await loadData();
            setEditingItem(updated);
            setSelectedFile(null);
        } catch (err) {
            console.error('Upload failed', err);
            setError('Upload failed: ' + extractErrorMessage(err));
        } finally {
            setUploading(false);
        }
    }

    function fileUrl(item) {
        if (!item || (!(item.fileName || item.FileName))) return null;
        const fname = encodeURIComponent(item.fileName || item.FileName || 'file');
        return `${authConfig.api.urls.getMemberLibraryDocumentFile}/${encodeURIComponent(item.id)}/${fname}`;
    }

    return (
        <Container fluid>
            <Row className="mb-3 align-items-center">
                <Col><h5>Member Library Documents</h5></Col>
                <Col className="text-end">
                    <Button variant="primary" onClick={openAdd}>Add New</Button>
                </Col>
            </Row>

            {error && !showEditModal && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
            ) : (
                <Table bordered hover responsive>
                    <thead>
                        <tr>
                            <th style={{ cursor: 'pointer' }} onClick={() => changeSort('title')}>Title{sortField === 'title' ? (sortAsc ? ' ▲' : ' ▼') : ''}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => changeSort('category')}>Category{sortField === 'category' ? (sortAsc ? ' ▲' : ' ▼') : ''}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => changeSort('filename')}>File{sortField === 'filename' ? (sortAsc ? ' ▲' : ' ▼') : ''}</th>
                            <th style={{ width: 150 }}>Enabled</th>
                            <th style={{ width: 220 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!items || items.length === 0) && (
                            <tr><td colSpan="5" className="text-center text-muted">No documents found</td></tr>
                        )}
                        {([...items] || []).sort((a, b) => {
                            const get = (obj, f) => {
                                if (f === 'filename') return (obj.fileName || obj.FileName || '').toString();
                                return (obj[f] || '').toString();
                            };
                            const av = get(a, sortField);
                            const bv = get(b, sortField);
                            const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
                            return sortAsc ? cmp : -cmp;
                        }).map(it => (
                            <tr key={it.id}>
                                <td>{it.title}</td>
                                <td>{it.category}</td>
                                <td>{it.fileName || it.FileName || ''}</td>
                                <td>{it.enabled ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                                <td>
                                    <Button size="sm" variant="outline-primary" onClick={() => openEdit(it)} className="me-2"><FaEdit /></Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => deleteItem(it)} className="me-2"><FaRegTrashAlt /></Button>
                                    <Button size="sm" variant="outline-secondary" onClick={() => toggleEnabled(it)} className="me-2">{it.enabled ? <FaToggleOff /> : <FaToggleOn />}</Button>
                                    {fileUrl(it) && (
                                        <SecureFileLink type="button" allowdownload={false} variant="outline-info" url={fileUrl(it)} filename={it.fileName || it.FileName}><FaRegFileAlt /></SecureFileLink>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={showEditModal} onHide={closeModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem && editingItem.id ? 'Edit Document' : 'Add Document'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingItem ? (
                        <>
                            {error && showEditModal && <Alert variant="danger">{error}</Alert>}
                            <Form>
                                <Form.Group className="mb-2">
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control name="title" value={editingItem.title || ''} onChange={onFieldChange} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control as="textarea" rows={4} name="description" value={editingItem.description || ''} onChange={onFieldChange} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Category</Form.Label>
                                    <Form.Control name="category" value={editingItem.category || ''} onChange={onFieldChange} />
                                </Form.Group>

                                <Form.Group className="mb-2">
                                    <Form.Check type="checkbox" label="Enabled" name="enabled" checked={!!editingItem.enabled} onChange={onFieldChange} />
                                </Form.Group>

                                <hr />
                                <Form.Label>File attachment</Form.Label>
                                <Form.Group controlId="fileInput" className="mb-2">
                                    <Form.Control type="file" accept="application/pdf" onChange={onFileChange} />
                                </Form.Group>
                                {editingItem.id && (editingItem.fileName || editingItem.FileName) && (
                                    <div className="mb-2"><strong>Current file:</strong> {editingItem.fileName || editingItem.FileName}</div>
                                )}
                                {selectedFile && (
                                    <div className="mb-2"><strong>Selected file:</strong> {selectedFile.name}</div>
                                )}
                                {!editingItem.id ? (
                                    <div className="text-muted">Select a file now. It will be uploaded when you save the new document.</div>
                                ) : (
                                    <>
                                        <div className="mb-2">
                                            <Button variant="primary" size="sm" onClick={uploadFile} disabled={!selectedFile || uploading}>{uploading ? <><Spinner animation="border" size="sm" /> Uploading...</> : 'Upload'}</Button>
                                            {(editingItem.fileName || editingItem.FileName) && (
                                                <SecureFileLink type="button" allowdownload={false} variant="outline-info" className="ms-2" url={fileUrl(editingItem)} filename={editingItem.fileName || editingItem.FileName}>Open</SecureFileLink>
                                            )}
                                        </div>
                                    </>
                                )}

                            </Form>
                            </>
                            ) : (
                            <div className="text-center"><Spinner animation="border" /></div>
                    )}
                        
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
                    <Button variant="primary" onClick={saveItem} disabled={saving}>{saving ? <><Spinner animation="border" size="sm" /> Saving...</> : 'Save'}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default MemberDocumentLibraryAdminPage;
